import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ClearAll as ClearIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  LocalShipping as TruckIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  formatFboDate,
  formatFboFloat,
  formatFboNumber,
  getTruckDate,
  getTruckId,
  getTruckRegion,
} from './fboSupplyPlanTruckRows.mjs';
import {
  getFboTruckItemName,
  sortFboTruckItemsByArticle,
} from './fboTruckItemList.mjs';
import {
  FBO_TRUCK_SORT,
  FBO_TRUCK_STATUS,
  getFboTruckLoadMeta,
  prepareFboTruckRows,
} from './fboTruckList.mjs';

const truckItems = (truck) => truck?.items || truck?.composition || truck?.rows || [];
const itemPallets = (item) => {
  if (item?.pallets != null) return item.pallets;
  const qty = Number(item?.qty ?? item?.quantity ?? item?.to_supply);
  const unitsPerPallet = Number(item?.units_per_pallet);
  return Number.isFinite(qty) && unitsPerPallet > 0 ? qty / unitsPerPallet : null;
};

const STATUS_META = {
  [FBO_TRUCK_STATUS.FULL]: { label: 'Полная', color: 'success' },
  [FBO_TRUCK_STATUS.UNDERLOADED]: { label: 'Недогруз', color: 'warning' },
  [FBO_TRUCK_STATUS.OVERLOADED]: { label: 'Перегруз', color: 'error' },
};

const SORT_OPTIONS = [
  { value: FBO_TRUCK_SORT.DATE_ASC, label: 'Сначала ближайшие' },
  { value: FBO_TRUCK_SORT.DATE_DESC, label: 'Сначала поздние' },
  { value: FBO_TRUCK_SORT.REGION, label: 'По округу' },
  { value: FBO_TRUCK_SORT.UNDERLOADED, label: 'Сначала недогруженные' },
  { value: FBO_TRUCK_SORT.FILLED, label: 'Сначала заполненные' },
];

const STATUS_OPTIONS = [
  { value: FBO_TRUCK_STATUS.ALL, label: 'Все' },
  { value: FBO_TRUCK_STATUS.FULL, label: 'Полные' },
  { value: FBO_TRUCK_STATUS.UNDERLOADED, label: 'Недогруз' },
  { value: FBO_TRUCK_STATUS.OVERLOADED, label: 'Перегруз' },
];

const dateParts = (value) => {
  if (!value) return { date: '—', weekday: '' };
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return { date: formatFboDate(value), weekday: '' };
  return {
    date: date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }).replace('.', ''),
    weekday: date.toLocaleDateString('ru-RU', { weekday: 'long' }),
  };
};

function FboTruckItems({ truck, mobile }) {
  const items = useMemo(
    () => sortFboTruckItemsByArticle(truckItems(truck)),
    [truck],
  );
  if (!items.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 1 }}>
        Состав фуры пуст.
      </Typography>
    );
  }

  if (mobile) {
    return (
      <Stack spacing={1} aria-label="Состав фуры">
        {items.map((item, index) => (
          <Paper
            key={item.id ?? `${item.barcode || item.nm_id || index}_${index}`}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 1.5 }}
          >
            <Typography variant="bodyMedium" fontWeight={700}>{getFboTruckItemName(item)}</Typography>
            <Typography variant="bodySmall" color="text.secondary">
              {[item.nm_id ? `nmID ${item.nm_id}` : null, item.barcode ? `ШК ${item.barcode}` : null]
                .filter(Boolean).join(' · ') || 'Идентификаторы не указаны'}
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="labelSmall" color="text.secondary">Количество</Typography>
                <Typography variant="bodyMedium" fontWeight={700}>{formatFboNumber(item.qty ?? item.quantity ?? item.to_supply)}</Typography>
              </Box>
              <Box>
                <Typography variant="labelSmall" color="text.secondary">Палеты</Typography>
                <Typography variant="bodyMedium" fontWeight={700}>{formatFboFloat(itemPallets(item))}</Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <TableContainer>
      <Table size="small" aria-label="Состав фуры">
        <TableHead>
          <TableRow>
            <TableCell>Товар / артикул</TableCell>
            <TableCell>nmID</TableCell>
            <TableCell>Баркод</TableCell>
            <TableCell align="right">Количество</TableCell>
            <TableCell align="right">Палеты</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <TableRow key={item.id ?? `${item.barcode || item.nm_id || index}_${index}`} hover>
              <TableCell>{getFboTruckItemName(item)}</TableCell>
              <TableCell>{item.nm_id || '—'}</TableCell>
              <TableCell>{item.barcode || '—'}</TableCell>
              <TableCell align="right">{formatFboNumber(item.qty ?? item.quantity ?? item.to_supply)}</TableCell>
              <TableCell align="right">{formatFboFloat(itemPallets(item))}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function FboTruckList({
  truckPlan,
  readOnly = true,
  editable = false,
  onTruckOpen,
  expandedIds,
  onExpandedChange,
  planKey,
  loading = false,
  emptyMessage = 'План фур пока пуст',
  busy = false,
}) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('lg'));
  const controlled = expandedIds !== undefined;
  const [internalExpanded, setInternalExpanded] = useState([]);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState(FBO_TRUCK_SORT.DATE_ASC);
  const [status, setStatus] = useState(FBO_TRUCK_STATUS.ALL);
  const [region, setRegion] = useState('');
  const previousPlanKey = useRef(planKey);
  const sourceTrucks = useMemo(() => truckPlan?.trucks || [], [truckPlan?.trucks]);
  const regionOrder = useMemo(() => truckPlan?.regions || [], [truckPlan?.regions]);
  const sourceRows = useMemo(
    () => prepareFboTruckRows(sourceTrucks, { regionOrder }),
    [regionOrder, sourceTrucks],
  );
  const regionOptions = useMemo(
    () => [...new Set(sourceTrucks.map(getTruckRegion).filter(Boolean).map(String))]
      .sort((left, right) => left.localeCompare(right, 'ru')),
    [sourceTrucks],
  );
  const rows = useMemo(
    () => prepareFboTruckRows(sourceTrucks, {
      regionOrder,
      query,
      status,
      region,
      sortBy,
    }),
    [query, region, regionOrder, sortBy, sourceTrucks, status],
  );
  const groupedRows = useMemo(
    () => {
      if (![FBO_TRUCK_SORT.DATE_ASC, FBO_TRUCK_SORT.DATE_DESC].includes(sortBy)) return null;
      return rows.reduce((groups, row) => {
        const date = getTruckDate(row.truck) || '';
        const last = groups[groups.length - 1];
        if (last?.date === date) last.rows.push(row);
        else groups.push({ date, rows: [row] });
        return groups;
      }, []);
    },
    [rows, sortBy],
  );
  const activeFilters = Boolean(
    query
    || region
    || status !== FBO_TRUCK_STATUS.ALL
    || sortBy !== FBO_TRUCK_SORT.DATE_ASC
  );
  const currentExpanded = controlled ? expandedIds : internalExpanded;
  const expandedSet = useMemo(() => new Set((currentExpanded || []).map(String)), [currentExpanded]);

  useEffect(() => {
    if (previousPlanKey.current === planKey) return;
    previousPlanKey.current = planKey;
    if (!controlled) setInternalExpanded([]);
    onExpandedChange?.([]);
  }, [controlled, onExpandedChange, planKey]);

  useEffect(() => {
    const available = new Set(sourceRows.map((row) => row.key));
    const next = [...expandedSet].filter((key) => available.has(key));
    if (next.length === expandedSet.size) return;
    if (!controlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  }, [controlled, expandedSet, onExpandedChange, sourceRows]);

  const resetFilters = () => {
    setQuery('');
    setSortBy(FBO_TRUCK_SORT.DATE_ASC);
    setStatus(FBO_TRUCK_STATUS.ALL);
    setRegion('');
  };

  const toggleExpanded = (key, isExpanded) => {
    const next = new Set(expandedSet);
    if (isExpanded) next.add(key);
    else next.delete(key);
    const values = [...next];
    if (!controlled) setInternalExpanded(values);
    onExpandedChange?.(values);
  };

  if (loading) {
    return <Stack spacing={1}>{[0, 1, 2].map((key) => <Skeleton key={key} variant="rounded" height={86} />)}</Stack>;
  }

  if (!sourceRows.length) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
        <TruckIcon color="primary" sx={{ mb: 1, fontSize: 36 }} />
        <Typography variant="titleMedium" fontWeight={700}>В плане нет фур</Typography>
        <Typography variant="bodyMedium" color="text.secondary" sx={{ mt: 0.5 }}>{emptyMessage}</Typography>
      </Paper>
    );
  }

  const canEdit = editable && !readOnly && Boolean(onTruckOpen);
  const renderTruck = ({ truck, key, matchedInItems }) => {
    const load = getFboTruckLoadMeta(truck);
    const truckStatus = STATUS_META[load.status];
    const items = truckItems(truck);
    const displayDate = dateParts(getTruckDate(truck));
    const safeKey = key.replace(/[^a-zA-Z0-9_-]/g, '-');
    const progressValue = load.percent == null ? 0 : Math.min(100, Math.max(0, load.percent));
    const regionName = getTruckRegion(truck) || 'Округ не указан';
    const editLabel = `Редактировать фуру ${regionName} от ${formatFboDate(getTruckDate(truck))}`;
    const openTruck = (event) => {
      event.stopPropagation();
      onTruckOpen(getTruckId(truck), truck);
    };

    return (
      <Accordion
        key={key}
        expanded={expandedSet.has(key)}
        onChange={(_, value) => toggleExpanded(key, value)}
        disableGutters
        elevation={0}
        sx={(muiTheme) => ({
          border: `1px solid ${muiTheme.palette.outlineVariant || muiTheme.palette.divider}`,
          bgcolor: muiTheme.palette.surfaceContainerLow || muiTheme.palette.background.paper,
          '&:before': { display: 'none' },
          borderRadius: '12px !important',
          overflow: 'hidden',
        })}
      >
        <AccordionSummary
          id={`truck-summary-${safeKey}`}
          aria-controls={`truck-content-${safeKey}`}
          expandIcon={<ExpandMoreIcon aria-hidden="true" />}
          sx={{
            minHeight: 88,
            px: { xs: 1.5, md: 2 },
            '& .MuiAccordionSummary-content': {
              my: 1.5,
              minWidth: 0,
              mr: { xs: 0.5, md: 1 },
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              ml: { xs: 0.5, md: 1 },
              flexShrink: 0,
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'grid',
              alignItems: 'center',
              columnGap: { md: 2, xl: 2.5 },
              rowGap: { xs: 1.25, md: 1.5 },
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: '88px repeat(10, minmax(0, 1fr)) auto',
                xl: '88px minmax(240px, 1fr) minmax(220px, auto) minmax(220px, 280px) auto',
              },
              gridTemplateAreas: {
                xs: '"date" "region" "metrics" "loadStatus" "edit"',
                md: `
                  "date region region region region region region region region region region edit"
                  "metrics metrics metrics metrics metrics loadStatus loadStatus loadStatus loadStatus loadStatus loadStatus loadStatus"
                `,
                xl: '"date region metrics loadStatus edit"',
              },
            }}
          >
            <Box sx={{ gridArea: 'date', width: 88, flexShrink: 0 }}>
              <Typography variant="titleLarge" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                {displayDate.date}
              </Typography>
              <Typography variant="labelMedium" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                {displayDate.weekday}
              </Typography>
            </Box>

            <Box sx={{ gridArea: 'region', minWidth: 0 }}>
              <Tooltip title={regionName} placement="top" enterDelay={500}>
                <Typography variant="titleMedium" fontWeight={700} noWrap>
                  {regionName}
                </Typography>
              </Tooltip>
              <Typography variant="bodySmall" color="text.secondary">
                {items.length} {items.length === 1 ? 'товарная позиция' : 'товарных позиций'}
              </Typography>
              {matchedInItems && (
                <Typography variant="labelSmall" color="primary.main">
                  Совпадение в составе
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                gridArea: 'metrics',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(max-content, 1fr))',
                gap: { xs: 1.5, sm: 2.5, xl: 2 },
                minWidth: 0,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="labelSmall" color="text.secondary">Вместимость</Typography>
                <Typography variant="bodyMedium" fontWeight={700}>{formatFboFloat(load.capacity)}</Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="labelSmall" color="text.secondary">Занято</Typography>
                <Typography variant="bodyMedium" fontWeight={700}>{formatFboFloat(load.pallets)}</Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="labelSmall" color="text.secondary">
                  {load.free != null && load.free < 0 ? 'Сверх лимита' : 'Свободно'}
                </Typography>
                <Typography variant="bodyMedium" fontWeight={700}>
                  {formatFboFloat(load.free == null ? null : Math.abs(load.free))}
                </Typography>
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={{ xs: 1, md: 1.5 }}
              alignItems="center"
              sx={{ gridArea: 'loadStatus', minWidth: 0 }}
            >
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LinearProgress
                    variant="determinate"
                    value={progressValue}
                    color={truckStatus.color}
                    aria-label={`Загрузка фуры ${load.percent == null ? 'не рассчитана' : `${Math.round(load.percent)} процентов`}`}
                    sx={{ flex: 1, height: 8, borderRadius: 999 }}
                  />
                  <Typography variant="labelMedium" fontWeight={700} sx={{ minWidth: 42, textAlign: 'right' }}>
                    {load.percent == null ? '—' : `${Math.round(load.percent)}%`}
                  </Typography>
                </Stack>
              </Box>
              <Chip size="small" color={truckStatus.color} label={truckStatus.label} sx={{ flexShrink: 0 }} />
            </Stack>

            {canEdit && (
              <Box sx={{ gridArea: 'edit', justifySelf: { xs: 'stretch', md: 'end' } }}>
                <Tooltip title={editLabel} placement="top">
                  <span>
                    <IconButton
                      color="primary"
                      disabled={busy}
                      aria-label={editLabel}
                      onClick={openTruck}
                      onFocus={(event) => event.stopPropagation()}
                      sx={{ display: { xs: 'none', md: 'inline-flex', xl: 'none' }, width: 48, height: 48 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  disabled={busy}
                  aria-label={editLabel}
                  sx={{
                    minHeight: 44,
                    width: { xs: '100%', md: 'auto' },
                    display: { md: 'none', xl: 'inline-flex' },
                    whiteSpace: 'nowrap',
                  }}
                  onClick={openTruck}
                  onFocus={(event) => event.stopPropagation()}
                >
                  Редактировать
                </Button>
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails
          id={`truck-content-${safeKey}`}
          aria-labelledby={`truck-summary-${safeKey}`}
          sx={{
            px: { xs: 1.5, md: 2 },
            pb: 2,
            pt: 1.5,
            borderTop: `1px solid ${theme.palette.outlineVariant || theme.palette.divider}`,
            bgcolor: 'background.paper',
          }}
        >
          <FboTruckItems truck={truck} mobile={mobile} />
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              label="Поиск"
              placeholder="Округ, дата, товар, nmID или barcode"
              fullWidth
              inputProps={{ 'aria-label': 'Поиск фур' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon aria-hidden="true" /></InputAdornment>
                ),
              }}
              sx={{ flex: { md: 2 } }}
            />
            <FormControl fullWidth sx={{ flex: { md: 1 }, minWidth: { md: 230 } }}>
              <InputLabel id="fbo-truck-sort-label">Сортировка</InputLabel>
              <Select
                labelId="fbo-truck-sort-label"
                value={sortBy}
                label="Сортировка"
                onChange={(event) => setSortBy(event.target.value)}
                inputProps={{ 'aria-label': 'Сортировка списка фур' }}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {regionOptions.length > 1 && (
              <Autocomplete
                options={regionOptions}
                value={region || null}
                onChange={(_, value) => setRegion(value || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Округ" inputProps={{ ...params.inputProps, 'aria-label': 'Фильтр по округу' }} />
                )}
                fullWidth
                sx={{ flex: { md: 1 }, minWidth: { md: 210 } }}
              />
            )}
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box sx={{ overflowX: 'auto', pb: 0.25 }}>
              <ToggleButtonGroup
                exclusive
                value={status}
                onChange={(_, value) => value && setStatus(value)}
                aria-label="Фильтр по статусу фуры"
                size="small"
                sx={{ '& .MuiToggleButton-root': { minHeight: 40, whiteSpace: 'nowrap', px: 1.5 } }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Typography variant="bodySmall" color="text.secondary" sx={{ ml: { sm: 'auto !important' }, whiteSpace: 'nowrap' }}>
              Показано {rows.length} из {sourceRows.length}
            </Typography>
            {activeFilters && (
              <Button
                startIcon={<ClearIcon />}
                onClick={resetFilters}
                aria-label="Сбросить фильтры и сортировку"
                sx={{ minHeight: 44 }}
              >
                Сбросить
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {!rows.length ? (
        <Paper
          variant="outlined"
          sx={(muiTheme) => ({
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            bgcolor: alpha(muiTheme.palette.primary.main, 0.03),
          })}
        >
          <SearchIcon color="primary" sx={{ mb: 1, fontSize: 36 }} />
          <Typography variant="titleMedium" fontWeight={700}>По фильтрам ничего не найдено</Typography>
          <Typography variant="bodyMedium" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
            Измените запрос, статус или округ.
          </Typography>
          <Button variant="outlined" startIcon={<ClearIcon />} onClick={resetFilters} sx={{ minHeight: 44 }}>
            Сбросить фильтры
          </Button>
        </Paper>
      ) : groupedRows ? (
        <Stack spacing={2.5}>
          {groupedRows.map((group) => (
            <Box key={group.date || 'no-date'}>
              <Typography variant="titleSmall" color="text.secondary" sx={{ mb: 1, px: 0.5 }}>
                {formatFboDate(group.date)} · {group.rows.length} фур
              </Typography>
              <Stack spacing={1}>{group.rows.map(renderTruck)}</Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Stack spacing={1}>{rows.map(renderTruck)}</Stack>
      )}
    </Stack>
  );
}

export default FboTruckList;
